/**
 * Read-only display of an auth-letter extraction result.
 *
 * Renders the letter-level fields as a summary grid and the per-J-code data as
 * a table. Purely presentational — the parent owns running the extraction.
 * @module ExtractionResult
 */
import { AuthLetterUploadExtraction } from "../../../../../api/services/authLetterAgentTestService";

interface Props {
  result: AuthLetterUploadExtraction;
}

type Row = Record<string, unknown>;

/** Letter-level fields (same for every J-code) shown as a summary grid. */
const SUMMARY_FIELDS: {
  key: keyof AuthLetterUploadExtraction;
  label: string;
}[] = [
  { key: "order_id", label: "Order ID" },
  { key: "payer_name", label: "Coverage Payer Name" },
  { key: "extracted_payer_name", label: "Payer Name" },
  { key: "provider", label: "Provider" },
  { key: "member_id", label: "Member ID" },
  { key: "patient_name", label: "Patient Name" },
  { key: "auth_number", label: "Auth Number" },
  { key: "icd10_code", label: "ICD-10" },
  { key: "source_portal", label: "Source Portal" },
  { key: "auth_initiated_date", label: "Auth Initiated" },
];

/** Per-J-code table columns. */
const JCODE_COLUMNS: { key: string; label: string }[] = [
  { key: "j_code", label: "J-Code" },
  { key: "auth_status", label: "Auth Status" },
  { key: "start_date", label: "Start Date" },
  { key: "end_date", label: "End Date" },
  { key: "units_approved", label: "Units" },
  { key: "num_visits", label: "Visits" },
  { key: "is_denial", label: "Denial?" },
];

/**
 * Coerces an extraction value to a display string.
 *
 * @param v - Raw value from the extraction result
 * @return String form, or `""` for null/undefined/empty values
 */
const cell = (v: unknown): string =>
  v === null || v === undefined || v === "" ? "" : String(v);

/**
 * Builds the per-J-code rows (units merged in) for display.
 *
 * @param r - The extraction result
 * @return One row per J-code, with unit fields flattened in
 */
const jcodeRows = (r: AuthLetterUploadExtraction): Row[] =>
  (r.j_code_details ?? []).map((d) => {
    const u = r.units_map?.[d.j_code] ?? {};
    return {
      j_code: d.j_code,
      auth_status: d.auth_status,
      start_date: d.start_date,
      end_date: d.end_date,
      units_approved: u.units_approved,
      num_visits: u.num_visits,
      is_denial: u.is_denial,
    };
  });

/**
 * Renders the summary grid and J-code table for an extraction result.
 *
 * @param props.result - The extraction to display
 */
const ExtractionResult = ({ result }: Props) => {
  const rows = jcodeRows(result);

  return (
    <div className="mt-4 flex flex-col gap-4">
      {/* Letter summary */}
      <div className="rounded-lg border border-primaryGray-14 bg-white p-3">
        <div className="mb-2 text-tiny font-bold uppercase tracking-wide text-primaryGray-6">
          Letter details
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-4">
          {SUMMARY_FIELDS.map((f) => (
            <div key={f.key} className="flex flex-col">
              <span className="text-tiny text-primaryGray-6">{f.label}</span>
              <span className="break-words text-sm font-semibold text-primaryGray-1">
                {cell(result[f.key]) || "—"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Per-J-code table */}
      <div>
        <div className="mb-2 text-tiny font-bold uppercase tracking-wide text-primaryGray-6">
          J-Codes ({rows.length})
        </div>
        <div className="overflow-x-auto rounded-lg border border-primaryGray-14 bg-white">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-primaryGray-16 text-left">
                {JCODE_COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className="whitespace-nowrap border-b border-primaryGray-14 px-4 py-2 font-semibold text-primaryGray-1"
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={JCODE_COLUMNS.length}
                    className="px-4 py-3 text-center text-primaryGray-6"
                  >
                    No J-codes were extracted from this letter.
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-primaryGray-14 last:border-0"
                  >
                    {JCODE_COLUMNS.map((c) => (
                      <td
                        key={c.key}
                        className="whitespace-nowrap px-4 py-2 text-primaryGray-1"
                      >
                        {c.key === "is_denial" ? (
                          <span
                            className={`rounded-full px-2 py-0.5 text-tiny font-semibold ${
                              row.is_denial
                                ? "bg-red-100 text-red-600"
                                : "bg-primaryGreen-11 text-primaryGreen-4"
                            }`}
                          >
                            {row.is_denial ? "Denied" : "No"}
                          </span>
                        ) : (
                          cell(row[c.key]) || (
                            <span className="text-primaryGray-6">—</span>
                          )
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExtractionResult;
